import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { TAG_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import {
  NEEDL_BOOKMARK_TAG,
  ONE_MINUTE,
  QUERY_KEY,
} from '@/components/UserTags/common';
import { useDetectChannelId } from '@/features/Channels/hooks/useDetectChannelId';
import { useFetchChannel } from '@/features/Channels/hooks/useFetchChannel';

type DocumentTagsResponse = {
  results: DocumentTagsType;
};

type DocumentTagsType = {
  user_tags: string[];
  shared_tags: string[];
};

export const useDocumentTags = (deeplink: string) => {
  const queryClient = useQueryClient();
  const { channelId } = useDetectChannelId();

  const { channel } = useFetchChannel(channelId);

  const query = useQuery(
    [QUERY_KEY, deeplink, ...(channelId ? [channelId] : [])],
    ({ signal }) =>
      axiosInstance.get<DocumentTagsResponse>(TAG_API_ROUTES.index(), {
        params: {
          deep_link: deeplink,
          ...(channelId &&
            channel?.role === 'member' && { channel_id: channelId }),
        },
        signal,
      }),
    {
      enabled: !!deeplink && (channelId ? !!channel : true),
      select: (data) => {
        const tags = data.data.results ?? {
          user_tags: [],
          shared_tags: [],
        };

        return {
          tags: tags?.user_tags ?? [],
          userTags:
            tags?.user_tags?.filter((tag) => tag !== NEEDL_BOOKMARK_TAG) ?? [],
          sharedTags:
            tags?.shared_tags?.filter((tag) => tag !== NEEDL_BOOKMARK_TAG) ??
            [],
        };
      },
      staleTime: ONE_MINUTE,
    }
  );

  React.useEffect(() => {
    queryClient.invalidateQueries([QUERY_KEY, deeplink, channelId]);
  }, [deeplink, queryClient, channelId]);

  return query;
};
